// src/components/TeamsWizard.jsx
// UPDATED VERSION - Uses stable UUIDs as object keys (matches Settings.jsx)
// FIXED: matchArea values now match UnifiedPitchAllocator expectations
// FIXED: Duration options now have correct values
import React, { useState, useEffect } from 'react';
import { db, auth } from '../utils/firebase';
import { collection, addDoc, query, where, getDocs, serverTimestamp, getDoc, doc, setDoc } from 'firebase/firestore';

// UUID Generator - matches Settings.jsx format
const generateUUID = () => {
  return 'team_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
};

const TEAM_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Teal', value: '#14b8a6' }
];

// Match area options - values must match UnifiedPitchAllocator expectations exactly
const PITCH_SIZE_OPTIONS = [
  { value: 'Full Pitch', label: 'Full Pitch', description: '11v11 - Full pitch' },
  { value: 'Half Pitch', label: 'Half Pitch', description: '9v9 - Half of a full size pitch' },
  { value: 'Quarter pitch', label: 'Quarter Pitch', description: '7v7- Qtr of a full size pitch' },
  { value: 'Eighth pitch', label: 'Single Section', description: '4v4 - 8th of a full size pitch' }
];

// Duration options - all values from UnifiedPitchAllocator
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 20, label: '20 minutes' },
  { value: 25, label: '25 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 35, label: '35 minutes' },
  { value: 40, label: '40 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 50, label: '50 minutes' },
  { value: 55, label: '55 minutes' },
  { value: 60, label: '60 minutes' },
  { value: 65, label: '65 minutes' },
  { value: 70, label: '70 minutes' },
  { value: 75, label: '75 minutes' },
  { value: 80, label: '80 minutes' },
  { value: 85, label: '85 minutes' },
  { value: 90, label: '90 minutes' },
  { value: 105, label: '105 minutes' },
  { value: 120, label: '120 minutes' }
];

function TeamsWizard({ clubId, onComplete }) {
  const [teams, setTeams] = useState({});  // Changed: Now object with team names as keys
  const [newTeamName, setNewTeamName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TEAM_COLORS[0].value);
  const [pitchSize, setPitchSize] = useState('Full Pitch');
  const [gameDuration, setGameDuration] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load existing teams from settings/general
  useEffect(() => {
    loadTeams();
  }, [clubId]);

  const loadTeams = async () => {
    if (!clubId) return;

    try {
      const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const teamsData = data.teams || {};
        
        // Handle both old array format and object format (UUID or name-based)
        if (Array.isArray(teamsData)) {
          // Convert array to object with UUIDs as keys
          const teamsObject = {};
          teamsData.forEach(team => {
            if (team.name) {
              const teamId = team.id || generateUUID();
              teamsObject[teamId] = { ...team, id: teamId };
            }
          });
          setTeams(teamsObject);
        } else {
          // Already in object format - ensure each team has an id field
          const teamsWithIds = {};
          Object.entries(teamsData).forEach(([key, team]) => {
            teamsWithIds[key] = { ...team, id: key };
          });
          setTeams(teamsWithIds);
        }
      }
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  const handleAddTeam = async () => {
    const trimmedName = newTeamName.trim();
    
    if (!trimmedName) {
      setError('Please enter a team name');
      return;
    }

    if (!clubId) {
      setError('Club ID not found');
      return;
    }

    // Check for duplicate team name (case-insensitive) by checking all team names
    const existingTeamNames = Object.values(teams).map(t => (t.name || '').toLowerCase());
    if (existingTeamNames.includes(trimmedName.toLowerCase())) {
      setError('A team with this name already exists');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate a new UUID for this team
      const teamId = generateUUID();
      
      const teamData = {
        id: teamId, // Store ID in the team object
        name: trimmedName,
        color: selectedColor,
        matchArea: pitchSize, // Now stores exact value like 'Full Pitch', 'Half Pitch', etc.
        matchDuration: gameDuration
      };

      // Get current teams
      const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
      const settingsDoc = await getDoc(settingsRef);
      
      let currentTeams = {};
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        currentTeams = data.teams || {};
        
        // Handle old array format - convert to UUID-based object
        if (Array.isArray(currentTeams)) {
          const teamsObject = {};
          currentTeams.forEach(team => {
            if (team.name) {
              const existingId = team.id || generateUUID();
              teamsObject[existingId] = { ...team, id: existingId };
            }
          });
          currentTeams = teamsObject;
        }
      }

      // Add new team using UUID as key
      currentTeams[teamId] = teamData;

      // Save back to Firestore
      await setDoc(settingsRef, {
        teams: currentTeams,
        lastUpdated: new Date().toISOString()
      }, { merge: true });
      
      // Reload teams
      await loadTeams();
      
      // Reset form
      setNewTeamName('');
      setSelectedColor(TEAM_COLORS[0].value);
      setPitchSize('Full Pitch');
      setGameDuration(60);
      setError('');
    } catch (error) {
      console.error('Error adding team:', error);
      setError('Failed to add team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (Object.keys(teams).length === 0) {
      setError('Please add at least one team to continue');
      return;
    }
    onComplete();
  };

  const handleSkip = () => {
    onComplete();
  };

  // Helper to get display label for matchArea
  const getMatchAreaDisplay = (matchArea) => {
    // Handle new format
    const option = PITCH_SIZE_OPTIONS.find(o => o.value === matchArea);
    if (option) return option.label;
    
    // Handle legacy uppercase format
    if (matchArea === 'FULL') return 'Full Pitch';
    if (matchArea === 'HALF') return 'Half Pitch';
    if (matchArea === 'QUARTER') return 'Quarter Pitch';
    if (matchArea === 'EIGHTH') return 'Single Section';
    
    return matchArea || 'Full Pitch';
  };

  // Convert teams object to array for display
  const teamsArray = Object.values(teams);

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Add Team Form */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '32px'
      }}>
        <h3 style={{
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: '600',
          color: '#1f2937'
        }}>
          Add a Team
        </h3>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Team Name
          </label>
          <input
            type="text"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            placeholder="e.g. Under 12s, First Team, Reserves"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddTeam();
              }
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '12px'
          }}>
            Team Color
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
            gap: '12px'
          }}>
            {TEAM_COLORS.map((color) => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                style={{
                  padding: '12px',
                  backgroundColor: color.value,
                  border: selectedColor === color.value ? '3px solid #1f2937' : '2px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  minHeight: '50px'
                }}
                onMouseEnter={(e) => {
                  if (selectedColor !== color.value) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span style={{
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}>
                  {color.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '4px'
            }}>
              Pitch Size
            </label>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              Select your team's pitch size on match day
            </p>
            <select
              value={pitchSize}
              onChange={(e) => setPitchSize(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              {PITCH_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '4px'
            }}>
              Match Duration
            </label>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              The length of your team's matches. e.g. 2x 30 minute halves, select 60 minutes
            </p>
            <select
              value={gameDuration}
              onChange={(e) => setGameDuration(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              {DURATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleAddTeam}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px 24px',
            backgroundColor: loading ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#3b82f6';
            }
          }}
        >
          {loading ? 'Adding...' : '+ Add Team'}
        </button>
      </div>

      {/* Teams List */}
      {teamsArray.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            Your Teams ({teamsArray.length})
          </h3>
          
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {teamsArray.map((team) => (
              <div
                key={team.id || team.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  backgroundColor: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: team.color,
                    borderRadius: '8px',
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '4px'
                  }}>
                    {team.name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {getMatchAreaDisplay(team.matchArea)} • {team.matchDuration} min games
                  </div>
                </div>
                <div style={{
                  fontSize: '24px',
                  color: '#10b981'
                }}>
                  ✓
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        justifyContent: teamsArray.length > 0 ? 'space-between' : 'center',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb'
      }}>
        {teamsArray.length === 0 ? (
          <button
            onClick={handleSkip}
            style={{
              padding: '14px 32px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#9ca3af';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            Skip for Now
          </button>
        ) : (
          <>
            <button
              onClick={handleSkip}
              style={{
                padding: '14px 32px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#9ca3af';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              I'll Add More Later
            </button>
            
            <button
              onClick={handleContinue}
              style={{
                padding: '14px 40px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Continue →
            </button>
          </>
        )}
      </div>

      {/* Helper Text */}
      <p style={{
        textAlign: 'center',
        fontSize: '14px',
        color: '#9ca3af',
        marginTop: '24px',
        fontStyle: 'italic'
      }}>
        {teamsArray.length === 0 
          ? "Don't worry, you can add teams anytime from the Teams page"
          : "You can always add more teams later from the Teams page"
        }
      </p>
    </div>
  );
}

export default TeamsWizard;
